import { z } from 'zod';

import { PublicOrgSchema } from './orgs';
import { PasswordSchema } from './requests';
import { MemberSchema } from './resources';

/** O link pessoal para completar o cadastro (`POST /members/{id}/profile-link`). */
export const ProfileLinkSchema = z
  .object({
    token: z.string().min(1),
    path: z.string().meta({ description: 'O caminho na aplicação: `/{igreja}/completar/{token}`.' }),
    expiresAt: z.iso.datetime().meta({ description: 'Até quando o link vale (30 dias).' }),
  })
  .meta({
    id: 'ProfileLink',
    description: 'Um link novo invalida o anterior; criar o acesso pelo link o encerra.',
  });

/** O que o formulário do link edita: contato e privado em branco ficam como estão. */
export const SelfProfileSchema = MemberSchema.pick({
  name: true,
  nickname: true,
  birthDate: true,
  sex: true,
  phone: true,
  email: true,
  civilStatus: true,
  profession: true,
  churchStatus: true,
  memberSince: true,
  joinedVia: true,
  baptismDate: true,
  originChurch: true,
  cpf: true,
  address: true,
  emergency: true,
  availability: true,
}).meta({
  id: 'SelfProfile',
  description:
    'Os dados que a pessoa completa pelo link. E-mail, telefone, CPF, endereço e contato de emergência não ' +
    'voltam ao formulário (o link pode ter sido repassado): em branco, ficam como estão.',
});

export const SelfProfileKnownSchema = z
  .object({
    email: z.string().meta({ description: 'O e-mail mascarado ("a•••@gmail.com"); `""` sem e-mail.' }),
    phone: z.string().meta({ description: 'O fim do telefone ("•••• 4567"); `""` sem telefone.' }),
    cpf: z.boolean(),
    address: z.boolean(),
    emergency: z.boolean(),
  })
  .meta({ id: 'SelfProfileKnown', description: 'O que já está guardado e não volta ao formulário.' });

export const ProfileFormSchema = z
  .object({
    org: PublicOrgSchema.omit({ registrationToken: true }),
    profile: SelfProfileSchema,
    known: SelfProfileKnownSchema,
    expiresAt: z.iso.datetime(),
  })
  .meta({ id: 'ProfileForm', description: 'O formulário do link de completar o cadastro.' });

export const ProfileSubmitSchema = z
  .object({
    profile: SelfProfileSchema,
    consent: z.literal(true).meta({ description: 'Autorização da LGPD — obrigatória para salvar.' }),
  })
  .meta({ id: 'ProfileSubmit' });

export const ProfileAccountRequestSchema = z
  .object({
    token: z.string().min(1),
    email: z.email().max(254),
    password: PasswordSchema,
    termsVersion: z.string().min(1).max(20).meta({
      description: 'A versão dos Termos e da Política aceita (a atual; senão 409 `terms_outdated`).',
    }),
  })
  .meta({
    id: 'ProfileAccountRequest',
    description:
      'Criar o acesso pelo link de completar: o e-mail do login, a senha e o aceite. Abre a sessão.',
  });

export type ProfileLink = z.infer<typeof ProfileLinkSchema>;
export type ProfileForm = z.infer<typeof ProfileFormSchema>;
export type ProfileSubmit = z.infer<typeof ProfileSubmitSchema>;
export type ProfileAccountRequest = z.infer<typeof ProfileAccountRequestSchema>;
